import { useCallback, useState } from "react";

import { Card } from "@/components/ui/card";

import { addDismissedBundleTaskId } from "@utils/dismissedBundleTasks";

import { PollingPhase, ResultPhase, UploadPhase } from "./Processes";

type FileItemProps = {
  file: File;
  userId: string;
  caseNumber: string;
  // STP-77: when present, mount directly into polling/result for an in-flight task
  // recovered from the backend, skipping the upload step.
  initialState?: { taskId: string; phase: "polling" | "result" };
  onToastClose?: () => void;
  onComplete?: (needsReview: boolean) => void;
  onDismiss?: () => void;
};

type Phase =
  | { step: "upload" }
  | { step: "polling"; taskId: string }
  | { step: "result"; taskId: string };

export const FileItem = ({
  file,
  userId,
  caseNumber,
  initialState,
  onToastClose,
  onComplete,
  onDismiss,
}: FileItemProps) => {
  const [phase, setPhase] = useState<Phase>(() =>
    initialState
      ? { step: initialState.phase, taskId: initialState.taskId }
      : { step: "upload" }
  );

  const handleTaskCreated = useCallback((taskId: string) => {
    setPhase({ step: "polling", taskId });
  }, []);

  const handleProcessingComplete = useCallback(() => {
    setPhase((prev) => {
      if (prev.step === "polling") {
        return { step: "result", taskId: prev.taskId };
      }
      return prev;
    });
  }, []);

  const handleComplete = useCallback(
    (needsReview: boolean) => onComplete?.(needsReview),
    [onComplete]
  );

  // STP-77: when the user dismisses a row that already has a task_id, persist
  // it so refresh-rehydration won't bring it back. We don't yet have a backend
  // revoke endpoint, so this is the best we can do client-side.
  const handleDismiss = useCallback(() => {
    if (phase.step === "polling" || phase.step === "result") {
      addDismissedBundleTaskId(phase.taskId);
    }
    onDismiss?.();
  }, [phase, onDismiss]);

  return (
    <Card className="gap-2 border-none p-2">
      {phase.step === "upload" && (
        <UploadPhase
          file={file}
          userId={userId}
          caseNumber={caseNumber}
          onTaskCreated={handleTaskCreated}
          onDismiss={handleDismiss}
        />
      )}

      {phase.step === "polling" && (
        <PollingPhase
          file={file}
          taskId={phase.taskId}
          onProcessingComplete={handleProcessingComplete}
          onDismiss={handleDismiss}
        />
      )}

      {phase.step === "result" && (
        <ResultPhase
          file={file}
          taskId={phase.taskId}
          caseNumber={caseNumber}
          onToastClose={onToastClose}
          onComplete={handleComplete}
          onDismiss={handleDismiss}
        />
      )}
    </Card>
  );
};
